<template>
  <div
    :id="uuid"
    class="p-6 md:p-8 bg-white rounded-xl"
    :class="[formData.blockShadow && 'shadow-lg']"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
      'background-color': formData.bgColor,
    }"
  >
    <h2
      v-if="formData.title && !(formData.hideOnEmbed && isInIframe)"
      class="text-2xl/[1] md:text-3xl/[1.20] font-bold mb-4 md:mb-6"
      :style="{
        textAlign: formData.titleAlign,
        color: formData.titleColor,
        fontWeight: formData.titleWeight,
        fontStyle: formData.titleStyle,
        fontSize: formData.titleSizePx,
        ...useEditorFontFamily(formData.titleFontFamily),
      }"
    >
      {{ formData.title }}
    </h2>
    <div
      class="cards-grid gap-6"
      :class="[
        gridColsClass,
        hasVisibleButtons && 'cards-grid_with-buttons',
        gridDisplayClass,
      ]"
    >
      <template
        v-for="(card, index) in formData.cards"
        :key="'cards-grid' + index"
      >
        <CardsItem
          :card="card"
          :is-bordered="formData.isBordered"
          :border-color="formData.borderColor"
          :is-shadowed="formData.isShadowed"
          :shadow-color="formData.shadowColor"
          :horizontal-padding="formData.horizontalPadding"
          :vertical-padding="formData.verticalPadding"
        />
      </template>
    </div>
    <div :class="sliderDisplayClass">
      <Swiper
        :slides-per-view="'auto'"
        :space-between="SLIDES_GAP"
        :pagination="{
          clickable: true,
          dynamicBullets: true,
          dynamicMainBullets: 10,
        }"
        :modules="swiperModules"
        class="swiper-custom"
        :class="formData.isShadowed && 'swiper-shadowed'"
      >
        <template
          v-for="(card, index) in formData.cards"
          :key="'cards-slider' + index"
        >
          <SwiperSlide
            class="!h-auto"
            :style="{
              padding: '0 4px',
              marginRight: SLIDES_GAP + 'px',
              width: getSlideWidth(formData.colsNumberOnMobile, SLIDES_GAP),
            }"
          >
            <CardsItem
              :card="card"
              :is-bordered="formData.isBordered"
              :border-color="formData.borderColor"
              :is-shadowed="formData.isShadowed"
              :shadow-color="formData.shadowColor"
              :horizontal-padding="formData.horizontalPadding"
              :vertical-padding="formData.verticalPadding"
            />
          </SwiperSlide>
        </template>
      </Swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import CardsItem from './CardsItem.vue'
import {
  getAdaptiveGridColsClass,
  getSlideWidth,
  shouldUseMediumGridLayout,
} from '~/components/base/utils'
import { usePageEditor } from '~/stores/page-editor'

const Swiper = (await import('swiper/vue')).Swiper
const SwiperSlide = (await import('swiper/vue')).SwiperSlide
const Grid = (await import('swiper/modules')).Grid
const Pagination = (await import('swiper/modules')).Pagination

const pageEditorStore = usePageEditor()

interface PropsInterface {
  uuid: string
  formData: any
  isInIframe?: boolean
}

const props = defineProps<PropsInterface>()
const SLIDES_GAP = computed(() => (props.formData.isShadowed ? 16 : 24))

const swiperModules = [Grid, Pagination]

const hasVisibleButtons = computed(() =>
  props.formData.cards?.some(
    (card: any) => card.cardType === 'text' && card.isButtonShown
  )
)

const usesMediumGridLayout = computed(() =>
  shouldUseMediumGridLayout(
    props.formData.colsNumberOnDesktop,
    props.formData.colsNumberOnMobile,
    props.formData.mediumColsMode
  )
)

const gridDisplayClass = computed(() => {
  if (!props.formData.isUseSliderOnMobile) {
    return 'grid'
  }

  return usesMediumGridLayout.value ? 'hidden sm:grid' : 'hidden md:grid'
})

const sliderDisplayClass = computed(() => {
  if (!props.formData.isUseSliderOnMobile) {
    return 'hidden'
  }

  return usesMediumGridLayout.value ? 'block sm:hidden' : 'block md:hidden'
})

const gridColsClass = computed(() => {
  return getAdaptiveGridColsClass(
    props.formData.cards?.length || 0,
    props.formData.colsNumberOnDesktop,
    props.formData.colsNumberOnMobile,
    props.formData.mediumColsMode,
    props.formData.colsNumberOnMedium
  )
})
</script>
<style>
@import 'swiper/css';
@import 'swiper/css/grid';
@import 'swiper/css/pagination';

@supports (grid-template-rows: subgrid) {
  .cards-grid_with-buttons > .cards-grid-item {
    display: grid;
    grid-row: span 2;
    grid-template-rows: subgrid;
  }

  .cards-grid_with-buttons > .cards-grid-item > .cards-grid-item__content {
    display: grid !important;
    grid-row: 1 / -1;
    grid-template-rows: subgrid;
  }

  .cards-grid_with-buttons > .cards-grid-item > .card-item-text,
  .cards-grid_with-buttons
    > .cards-grid-item
    > .cards-grid-item__content
    > .card-item-text {
    display: grid;
    grid-row: 1 / -1;
    grid-template-rows: subgrid;
  }

  .cards-grid_with-buttons .card-item-text__button {
    height: 100%;
    margin-top: 0;
  }

  .cards-grid_with-buttons .card-item-text__button > .editor-button {
    height: 100%;
  }

  .cards-grid_with-buttons .cards-grid-item__media {
    grid-row: 1 / -1;
  }
}
</style>
